import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../Button/Button";
import { Select } from "../Select/Select";
import {
	ShippingAddressFormModal,
	type ShippingAddressFormValues,
} from "../ShippingAddressFormModal/ShippingAddressFormModal";
import { useCompanyAddresses, useCreateCompanyAddress, type CompanyAddress } from "../../../hooks/use-companies";
import { formatAddress } from "./format-address";
import styles from "./ShippingAddressPicker.module.css";

function addressLabel(addr: CompanyAddress): string {
	const summary = formatAddress(addr);
	return summary ? `${addr.addressName ?? "-"} — ${summary}` : (addr.addressName ?? "-");
}

interface Props {
	companyId: string;
	value: string;
	onChange: (addressId: string, formatted: string) => void;
	disabled?: boolean;
}

export function ShippingAddressPicker({ companyId, value, onChange, disabled }: Props) {
	const { t } = useTranslation();
	const addresses = useCompanyAddresses(companyId, "shipping");
	const createAddress = useCreateCompanyAddress(companyId);
	const [modalOpen, setModalOpen] = useState(false);
	const [error, setError] = useState("");

	const items = addresses.data?.items ?? [];

	const handleSelect = (id: string) => {
		const addr = items.find((a) => a.id === id);
		onChange(id, addr ? formatAddress(addr) : "");
	};

	const handleCreate = (form: ShippingAddressFormValues) => {
		setError("");
		createAddress.mutate(
			{
				type: "shipping",
				addressName: form.addressName,
				addressName2: form.addressName2 || undefined,
				addressName3: form.addressName3 || undefined,
				postalCode: form.postalCode || undefined,
				city: form.city || undefined,
				state: form.state || undefined,
				country: form.country || undefined,
			},
			{
				onSuccess: (created) => {
					onChange(created.id, formatAddress(created));
					setModalOpen(false);
				},
				onError: (err: unknown) => {
					setError(err instanceof Error ? err.message : t("error"));
				},
			},
		);
	};

	return (
		<>
			<div className={styles.row}>
				<div className={styles.selectWrap}>
					<Select
						options={[
							{ value: "", label: t("selectShippingAddress") },
							...items.map((a) => ({ value: a.id, label: addressLabel(a) })),
						]}
						value={value}
						onChange={(e) => handleSelect(e.target.value)}
						disabled={disabled || addresses.isLoading}
					/>
				</div>
				<Button
					type="button"
					variant="secondary"
					onClick={() => setModalOpen(true)}
					disabled={disabled}
				>
					{t("newAddress")}
				</Button>
			</div>

			<ShippingAddressFormModal
				key={modalOpen ? "open" : "closed"}
				isOpen={modalOpen}
				onClose={() => setModalOpen(false)}
				title={t("newShippingAddress")}
				onSubmit={handleCreate}
				isSubmitting={createAddress.isPending}
				submitError={error}
			/>
		</>
	);
}
